import React, { MouseEventHandler, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { getCloudinaryUrl } from "@core-ui/helpers/Image";
import {
    RETRY,
    DEFAULT_ERROR_MESSAGE,
    DEFAULT_ERROR_TITLE,
    ERROR_IMAGE_ID_UX4,
    DATA_TEST_ID,
    TOING_ERROR_IMAGE_ID,
} from "./constants";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

import { isEmpty } from "@swiggy-private/js-utils";
import { SwgyHeadersKeys } from "@swiggy-private/core-types";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import {
    ErrorContainer,
    ErrorContent,
    ErrorImage,
    ErrorMsg,
    ErrorMsgAfterTitle,
    ErrorSecondMsg,
    ErrorTextContent,
    ErrorBackButton,
    ActionButton,
    ActionsSection,
} from "./styles";
import { LocalStorage } from "@core-ui/helpers/LocalStorage";
import QuickCheckoutError from "./QuickCheckoutError";

type ActionType = {
    onClick: VoidFunction;
    text?: string;
} | null;

type PrimaryActionType = {
    onClick: VoidFunction;
    text: string;
    timeDisabledFor?: number;
} | null;

export interface ErrorProps {
    pageImpressionEvent?: VoidFunction; // Callback for NR logging, page load processing etc
    action?: ActionType;
    imageId?: string;
    trim?: boolean;
    className?: string;
    contentClassName?: string;
    titleClassName?: string;
    imageClassName?: string;
    textContentClassName?: string;
    message?: string;
    secondMessage?: string;
    title?: string;
    primaryAction?: PrimaryActionType;
    dataTestId?: string;
    onBackCTA?: VoidFunction;
    isHalfHeightImage?: boolean;
    errorImageMaxWidth?: number;
    errorImageMaxHeight?: number;
    isQuickCheckoutError?: boolean;
}

const defaultAction = {
    onClick: (): void => window.location.reload(),
    text: RETRY,
};

enum SOURCE_CHANNEL {
    POP_SHELL_APP = "pop-shell-app",
    TOING_APP = "toing-app",
}

const GenericError: React.FunctionComponent<ErrorProps> = (props) => {
    const { primaryAction, action = defaultAction } = props;
    const { message = DEFAULT_ERROR_MESSAGE, title = DEFAULT_ERROR_TITLE, secondMessage = "" } = props;
    const intervalIdRef = useRef(-1);
    const [timeLeft, setTimeLeft] = useState(props?.primaryAction?.timeDisabledFor || 0);

    const isToingAppFlow = useMemo(() => {
        const headers = LocalStorage.getItem<{ [key in Partial<SwgyHeadersKeys>]: string }>("auth_headers");
        const xChannel = headers?.["x-channel"] ?? "";
        return xChannel === SOURCE_CHANNEL.TOING_APP;
    }, []);

    // Determine UX4 mode and other variants
    const isUx4 = true; // Based on the original logic, UX4 is always true for certain elements

    useEffectOnce(() => {
        props.pageImpressionEvent?.();
        if (props.primaryAction?.timeDisabledFor) {
            intervalIdRef.current = window.setInterval(() => {
                setTimeLeft((t) => t - 1);
            }, 1000);
        }
        return () => {
            clearInterval(intervalIdRef.current);
        };
    });

    useEffect(() => {
        if (timeLeft <= 0) {
            clearInterval(intervalIdRef.current);
        }
    }, [timeLeft]);

    const onClickPrimaryAction: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.preventDefault();
            if (props.primaryAction) {
                props.primaryAction.onClick();
            }
        },
        [props.primaryAction],
    );

    const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.preventDefault();
            if (action) {
                action.onClick();
            }
        },
        [action],
    );

    const handleBackClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.preventDefault();
            props?.onBackCTA?.();
        },
        [props],
    );

    const getImageUrl = useCallback((): string => {
        const imageId = isToingAppFlow ? TOING_ERROR_IMAGE_ID : ERROR_IMAGE_ID_UX4;

        return getCloudinaryUrl(props.imageId || imageId, {
            trim: props.trim ?? true,
        });
    }, [props.imageId, props.trim, isToingAppFlow]);

    if (props?.isQuickCheckoutError) {
        return (
            <QuickCheckoutError
                title={title}
                primaryActionText={primaryAction?.text}
                onClickPrimaryAction={onClickPrimaryAction}
            />
        );
    }

    return (
        <ErrorContainer className={props.className} data-testid={props.dataTestId}>
            {!isEmpty(props.onBackCTA) && (
                <ErrorBackButton onClick={handleBackClick} data-testid={DATA_TEST_ID.ERROR_BACK_BUTTON}>
                    <SpriteIconWeb iconName={"arrowBack24"} fillColor="text_Med_Emphasis" />
                </ErrorBackButton>
            )}
            <ErrorContent
                className={props.contentClassName}
                ux4={!props.imageId}
                hasImage={!!props.imageId}
                isToingAppFlow={isToingAppFlow}
            >
                <ErrorImage
                    className={props.imageClassName}
                    ux4={!props.imageId && !isToingAppFlow}
                    halfHeight={props.isHalfHeightImage}
                    crossOrigin="anonymous"
                    src={getImageUrl()}
                    alt={title}
                    maxWidth={props.errorImageMaxWidth}
                    maxHeight={props.errorImageMaxHeight}
                />
                <ErrorTextContent className={props.textContentClassName} ux4={isUx4}>
                    {title && (
                        <Text
                            data-testid={DATA_TEST_ID.ERROR_TITLE}
                            className={props.titleClassName}
                            variant="V2.heading2.neutral.bold"
                            color="text_Highest_Emphasis"
                        >
                            {title}
                        </Text>
                    )}
                    {title ? (
                        <ErrorMsgAfterTitle ux4={isUx4}>
                            <Text variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                                {message}
                            </Text>
                        </ErrorMsgAfterTitle>
                    ) : (
                        <ErrorMsg ux4={isUx4}>
                            <Text variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                                {message}
                            </Text>
                        </ErrorMsg>
                    )}
                    {secondMessage && (
                        <ErrorSecondMsg>
                            <Text variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                                {secondMessage}
                            </Text>
                        </ErrorSecondMsg>
                    )}
                </ErrorTextContent>
                {(primaryAction || action !== null) && (
                    <ActionsSection>
                        {primaryAction && (
                            <ActionButton
                                onClick={onClickPrimaryAction}
                                variant="fullwidth"
                                buttonType="primary"
                                buttonSize="large"
                                data-testid={DATA_TEST_ID.ERROR_PRIMARY_ACTION}
                            >
                                {primaryAction.text} {timeLeft > 0 ? `(${timeLeft}s)` : ""}
                            </ActionButton>
                        )}
                        {action != null && (
                            <ActionButton
                                onClick={onClick}
                                variant="fullwidth"
                                buttonType="secondary"
                                buttonSize="large"
                                data-testid={DATA_TEST_ID.ERROR_SECONDARY_ACTION}
                            >
                                {action.text || RETRY}
                            </ActionButton>
                        )}
                    </ActionsSection>
                )}
            </ErrorContent>
        </ErrorContainer>
    );
};

export default GenericError;
